import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { MoneyInput } from '../ui/money-input';
import { Textarea } from '../ui/textarea';
import CalendarModal from '../CalendarModal';
import UFSelectorModal from './UFSelectorModal';
import { Calendar, DollarSign, Plus, Package, X, ArrowRightLeft, TrendingUp, Info } from 'lucide-react';
import { parseMoney, formatDecimal } from '../../utils/format';
import { FunctionalUnitForSale } from '../../services/functionalUnitService';
import { CharCounter } from '../ui/char-counter';

interface Step2SaleDetailsProps {
    saleDate: string;
    currency: 'ARS' | 'USD';
    exchangeRate: string;
    comments: string;
    selectedUnits: FunctionalUnitForSale[];
    onSaleDateChange: (date: string) => void;
    onCurrencyChange: (currency: 'ARS' | 'USD') => void;
    onExchangeRateChange: (rate: string) => void;
    onCommentsChange: (comments: string) => void;
    onSelectedUnitsChange: (units: FunctionalUnitForSale[]) => void;
    closedPrices: { [key: number]: string };
    onClosedPricesChange: (prices: { [key: number]: string }) => void;
    saleData?: any;
}

const Step2SaleDetails: React.FC<Step2SaleDetailsProps> = ({
    saleDate,
    currency,
    exchangeRate,
    comments,
    selectedUnits,
    onSaleDateChange,
    onCurrencyChange,
    onExchangeRateChange,
    onCommentsChange,
    onSelectedUnitsChange,
    closedPrices,
    onClosedPricesChange,
    saleData
}) => {
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [isUFModalOpen, setIsUFModalOpen] = useState(false);

    const formatDisplayDate = (dateString: string) => {
        if (!dateString) return 'Seleccione una fecha';
        const date = new Date(dateString + 'T00:00:00');
        return date.toLocaleDateString('es-AR', {
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        });
    };

    const handleDateSelect = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        onSaleDateChange(`${year}-${month}-${day}`);
    };

    const handleClosedPriceChange = (unitId: number, value: number | null) => {
        onClosedPricesChange({
            ...closedPrices,
            [unitId]: value !== null ? String(value) : ''
        });
    };

    const handleClosedPriceBlur = (unitId: number) => {
        const stored = closedPrices[unitId] || '';
        if (!stored.trim()) return;
        const parsed = Number(stored);
        if (isNaN(parsed) || parsed <= 0) {
            onClosedPricesChange({ ...closedPrices, [unitId]: '' });
        }
    };

    const exchangeRateNum = parseFloat(exchangeRate) || 0;
    const total = selectedUnits.reduce((sum, unit) => {
        const listInCurrency = currency === 'ARS' ? unit.listPrice * exchangeRateNum : unit.listPrice;
        const closedPriceValue = closedPrices[unit.id] || '';
        const closedPriceNum = parseMoney(closedPriceValue);
        const final = closedPriceNum || listInCurrency;
        return sum + final;
    }, 0);

    return (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="p-6 lg:p-8 space-y-8">
                {/* Section header */}
                <div>
                    <h3 className="text-lg font-semibold text-foreground">Detalle de la Venta</h3>
                    <p className="text-sm text-muted-foreground mt-0.5">
                        Complete la información general de la venta
                    </p>
                </div>

                {/* Form fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Fecha de Boleto */}
                    <div className="space-y-2">
                        <Label htmlFor="saleDate" className="flex items-center gap-2 text-sm font-medium">
                            <Calendar className="w-4 h-4 text-muted-foreground" />
                            Fecha de Boleto
                        </Label>
                        <Button
                            id="saleDate"
                            variant="outline"
                            onClick={() => setIsCalendarOpen(true)}
                            className="w-full justify-start text-left font-normal h-10"
                        >
                            <Calendar className="w-4 h-4 mr-2 text-muted-foreground" />
                            <span className={saleDate ? 'text-foreground' : 'text-muted-foreground'}>
                                {formatDisplayDate(saleDate)}
                            </span>
                        </Button>
                    </div>

                    {/* Moneda */}
                    <div className="space-y-2">
                        <Label htmlFor="currency" className="flex items-center gap-2 text-sm font-medium">
                            <DollarSign className="w-4 h-4 text-muted-foreground" />
                            Moneda de Venta
                        </Label>
                        <Select value={currency} onValueChange={(value) => onCurrencyChange(value as 'ARS' | 'USD')}>
                            <SelectTrigger id="currency">
                                <SelectValue placeholder="Seleccione moneda" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="USD">
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs font-bold text-emerald-600 bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400 px-1.5 py-0.5 rounded">USD</span>
                                        <span>Dólar Estadounidense</span>
                                    </div>
                                </SelectItem>
                                <SelectItem value="ARS">
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs font-bold text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 px-1.5 py-0.5 rounded">ARS</span>
                                        <span>Peso Argentino</span>
                                    </div>
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Tipo de Cambio - Solo visible si es ARS */}
                    {currency === 'ARS' && (
                        <div className="space-y-2 md:col-span-2 max-w-sm">
                            <Label htmlFor="exchangeRate" className="flex items-center gap-2 text-sm font-medium">
                                <ArrowRightLeft className="w-4 h-4 text-muted-foreground" />
                                Tipo de Cambio
                                <span className="text-xs font-normal text-muted-foreground">(ARS por USD)</span>
                            </Label>
                            <MoneyInput
                                id="exchangeRate"
                                value={exchangeRate}
                                onValueChange={(value) => {
                                    onExchangeRateChange(value !== null ? String(value) : '');
                                }}
                                placeholder="Ej: 1.200,00"
                                currency="ARS"
                            />
                            {(!exchangeRate || parseFloat(exchangeRate) <= 0) && (
                                <p className="text-xs text-destructive font-medium">
                                    El tipo de cambio es obligatorio para ventas en ARS
                                </p>
                            )}
                            {exchangeRate && parseFloat(exchangeRate) > 0 && (
                                <p className="text-xs text-muted-foreground">
                                    Ingrese el valor del dólar en pesos argentinos
                                </p>
                            )}
                        </div>
                    )}
                </div>

                {/* Observaciones */}
                <div className="space-y-2">
                    <Label htmlFor="comments" className="text-xs font-medium text-foreground sm:text-sm">
                        Observaciones
                        <span className="text-xs font-normal text-muted-foreground ml-1">(opcional)</span>
                    </Label>
                    <Textarea
                        id="comments"
                        value={comments}
                        onChange={(e) => onCommentsChange(e.target.value)}
                        placeholder="Agregue observaciones o notas adicionales sobre la venta..."
                        maxLength={500}
                        size="md"
                    />
                    <CharCounter value={comments} max={500} />
                </div>

                {/* Divider */}
                <div className="border-t border-border" />

                {/* Functional Units Section */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h4 className="text-sm font-semibold text-foreground">Unidades Funcionales</h4>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                {selectedUnits.length > 0
                                    ? `${selectedUnits.length} unidad${selectedUnits.length !== 1 ? 'es' : ''} seleccionada${selectedUnits.length !== 1 ? 's' : ''}`
                                    : 'Ninguna unidad seleccionada'}
                            </p>
                        </div>
                        <Button
                            onClick={() => setIsUFModalOpen(true)}
                            size="sm"
                            variant={selectedUnits.length > 0 ? 'outline' : 'default'}
                            className="gap-2 shrink-0"
                        >
                            <Plus className="w-4 h-4" />
                            {selectedUnits.length > 0 ? 'Modificar' : 'Agregar Unidades'}
                        </Button>
                    </div>

                    {/* Nota explicativa */}
                    <div className="flex items-start gap-2 px-1 py-0.5">
                        <Info className="w-3.5 h-3.5 text-muted-foreground/70 mt-0.5 shrink-0" />
                        <p className="text-xs text-muted-foreground/80 leading-snug">
                            <span className="font-medium text-muted-foreground">Precio Cerrado:</span>{' '}
                            muestra el precio de lista como referencia. Hacé clic para editarlo y establecerlo como precio final de venta.
                        </p>
                    </div>

                    {/* Table */}
                    <div className="border border-border rounded-xl overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-border bg-muted/40">
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                            Proyecto
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                            UF
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden sm:table-cell">
                                            Tipo
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden md:table-cell">
                                            Sup. m²
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                            P. Lista
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                            Precio Cerrado ({currency})
                                        </th>
                                        <th className="px-4 py-3 w-10" />
                                    </tr>
                                </thead>
                                <tbody className="bg-card divide-y divide-border">
                                    {selectedUnits.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} className="px-4 py-14 text-center">
                                                <div className="flex flex-col items-center gap-3">
                                                    <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
                                                        <Package className="w-6 h-6 text-muted-foreground" />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium text-foreground">Sin unidades seleccionadas</p>
                                                        <p className="text-xs text-muted-foreground mt-0.5">Haga clic en "Agregar Unidades" para comenzar</p>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        selectedUnits.map((unit) => {
                                            const listPriceInCurrency = currency === 'ARS' && exchangeRateNum > 0
                                                ? unit.listPrice * exchangeRateNum
                                                : unit.listPrice;

                                            const closedPriceValue = closedPrices[unit.id] || '';
                                            const closedPriceNum = parseMoney(closedPriceValue) ?? (closedPriceValue ? Number(closedPriceValue) : null);

                                            const listPlaceholder = listPriceInCurrency.toLocaleString('es-AR', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            });

                                            const hasClosedPrice = closedPriceNum !== null;

                                            return (
                                                <tr key={unit.id} className="hover:bg-muted/20 transition-colors group">
                                                    <td className="px-4 py-3 text-sm text-muted-foreground">
                                                        {unit.projectName}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-foreground font-medium">
                                                        {unit.functionalUnitName}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-muted-foreground hidden sm:table-cell">
                                                        {unit.ufType}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-right text-muted-foreground hidden md:table-cell">
                                                        {formatDecimal(unit.totalSurface)}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-right tabular-nums font-medium">
                                                        <span className={hasClosedPrice
                                                            ? 'line-through text-red-400 dark:text-red-500'
                                                            : 'text-emerald-600 dark:text-emerald-400'
                                                        }>
                                                            {currency} {listPriceInCurrency.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div className="flex justify-end">
                                                            <MoneyInput
                                                                placeholder={listPlaceholder}
                                                                value={closedPriceNum !== null ? closedPriceNum : undefined}
                                                                onValueChange={(value) => handleClosedPriceChange(unit.id, value)}
                                                                onBlur={() => handleClosedPriceBlur(unit.id)}
                                                                currency={currency}
                                                                className={`w-40 text-sm placeholder:text-muted-foreground/35 placeholder:font-normal ${hasClosedPrice ? 'text-emerald-600 dark:text-emerald-400 font-semibold' : ''}`}
                                                            />
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        <button
                                                            onClick={() => {
                                                                const updatedUnits = selectedUnits.filter(u => u.id !== unit.id);
                                                                onSelectedUnitsChange(updatedUnits);
                                                            }}
                                                            className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                                                            title="Quitar unidad"
                                                        >
                                                            <X className="w-3.5 h-3.5" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Total Row */}
                        {selectedUnits.length > 0 && (
                            <div className="border-t border-border bg-muted/30 px-4 py-3">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                        <TrendingUp className="w-4 h-4" />
                                        <span>{selectedUnits.length} unidad{selectedUnits.length !== 1 ? 'es' : ''} · Total</span>
                                    </div>
                                    <div className="border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/40 rounded-lg px-3 py-1.5">
                                        <p className="text-base font-bold tabular-nums text-emerald-500 dark:text-emerald-400">
                                            {currency} {total.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Calendar Modal */}
            <CalendarModal
                open={isCalendarOpen}
                onOpenChange={setIsCalendarOpen}
                onSelectDate={handleDateSelect}
                selectedDate={saleDate ? new Date(saleDate + 'T00:00:00') : undefined}
            />

            {/* UF Selector Modal */}
            <UFSelectorModal
                open={isUFModalOpen}
                onOpenChange={setIsUFModalOpen}
                onSelectUnits={onSelectedUnitsChange}
                selectedUnits={selectedUnits}
            />
        </div>
    );
};

export default Step2SaleDetails;
